import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  CircularProgress, 
  Divider, 
  FormControl, 
  FormControlLabel, 
  IconButton, 
  InputAdornment, 
  MenuItem, 
  Radio, 
  RadioGroup, 
  Select, 
  Slider, 
  Stack, 
  Step, 
  StepLabel, 
  Stepper, 
  TextField, 
  Typography,
  Chip,
  Alert,
  Tooltip,
  Paper,
  useTheme
} from '@mui/material';
import { 
  Search as SearchIcon, 
  Add as AddIcon, 
  Remove as RemoveIcon,
  Info as InfoIcon,
  ContentCopy as ContentCopyIcon,
  Language as LanguageIcon,
  YouTube as YouTubeIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  Reddit as RedditIcon,
  Web as WebIcon,
  Settings as SettingsIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

// Import API functions
import { startScan, getScanStatus, getScanResults, fetchSuggestedKeywords } from '../../services/api';

// Function to poll scan status until complete
const pollScanStatus = async (scanId, onComplete, onError) => {
  try {
    const status = await getScanStatus(scanId);
    
    if (status.status === 'completed') {
      const results = await getScanResults(scanId);
      onComplete(results);
      return;
    } else if (status.status === 'error') {
      onError(status.error || 'An error occurred during the scan');
      return;
    }
    
    // Continue polling
    setTimeout(() => pollScanStatus(scanId, onComplete, onError), 2000);
  } catch (error) {
    onError(error.message || 'Failed to check scan status');
  }
};

const SimpleScanForm = ({ onScanComplete }) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [creatorName, setCreatorName] = useState('');
  const [contentType, setContentType] = useState('all');
  const [keywordType, setKeywordType] = useState('suggested');
  const [customKeywords, setCustomKeywords] = useState('');
  const [timeframeType, setTimeframeType] = useState('today');
  const [timeframeValue, setTimeframeValue] = useState(7);
  const [maxSearches, setMaxSearches] = useState(100);
  const [isStartingScan, setIsStartingScan] = useState(false);
  const [error, setError] = useState(null);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStartScan = async () => {
    setIsStartingScan(true);
    setError(null);
    
    try {
      // Prepare scan data
      const scanData = {
        creatorName,
        contentType,
        keywords: keywordType === 'suggested' ? 
          await getSuggestedKeywords() : 
          customKeywords.split(',').map(k => k.trim()).filter(Boolean),
        timeframe: timeframeType === 'today' ? 'today' : timeframeType === 'lifetime' ? 'lifetime' : { type: timeframeType, value: timeframeValue },
        maxSearches
      };
      
      // Start the scan
      const response = await startScan(scanData);
      
      // Poll for scan completion
      pollScanStatus(
        response.scanId,
        (result) => {
          if (onScanComplete) {
            onScanComplete(result);
          }
          setIsStartingScan(false);
        },
        (error) => {
          setError(`Scan failed: ${error}`);
          setIsStartingScan(false);
          console.error('Scan error:', error);
        }
      );
    } catch (err) {
      setError('Failed to start scan. Please try again.');
      console.error('Scan error:', err);
      setIsStartingScan(false);
    }
  };
  
  // Get suggested keywords from the API
  const getSuggestedKeywords = async () => {
    try {
      const response = await fetchSuggestedKeywords(creatorName);
      return response.keywords || [
        "onlyfans leaks",
        "leaked content",
        "private photos",
        "nude leaks",
        "xxx content"
      ];
    } catch (error) {
      console.error('Error fetching suggested keywords:', error);
      return [
        "onlyfans leaks",
        "leaked content",
        "private photos",
        "nude leaks",
        "xxx content"
      ];
    }
  };

  const steps = ['Creator Details', 'Content Settings', 'Review & Start'];

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 4 }}>
        <CardContent>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {activeStep === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Typography variant="h6" gutterBottom>Creator Information</Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Enter the name of the creator whose content you want to scan for leaks.
              </Typography>
              
              <TextField
                fullWidth
                label="Creator Name"
                variant="outlined"
                value={creatorName}
                onChange={(e) => setCreatorName(e.target.value)}
                margin="normal"
                placeholder="Enter creator name"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!creatorName.trim()}
                  endIcon={<ArrowForwardIcon />}
                >
                  Next
                </Button>
              </Box>
            </motion.div>
          )}
          
          {activeStep === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Typography variant="h6" gutterBottom>Content Settings</Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Configure the scan settings to find leaked content.
              </Typography>
              
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>Content Type</Typography>
              <RadioGroup
                row
                value={contentType}
                onChange={(e) => setContentType(e.target.value)}
              >
                <FormControlLabel value="all" control={<Radio />} label="All Content" />
                <FormControlLabel value="video" control={<Radio />} label="Videos" />
                <FormControlLabel value="image" control={<Radio />} label="Images" />
                <FormControlLabel value="text" control={<Radio />} label="Text" />
              </RadioGroup>
              
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>Keywords</Typography>
              <RadioGroup
                value={keywordType}
                onChange={(e) => setKeywordType(e.target.value)}
              >
                <FormControlLabel 
                  value="suggested" 
                  control={<Radio />} 
                  label="Use suggested keywords based on creator name" 
                />
                <FormControlLabel 
                  value="custom" 
                  control={<Radio />} 
                  label="Use custom keywords" 
                />
              </RadioGroup>
              
              {keywordType === 'custom' && (
                <TextField
                  fullWidth
                  label="Custom Keywords"
                  variant="outlined"
                  value={customKeywords}
                  onChange={(e) => setCustomKeywords(e.target.value)}
                  margin="normal"
                  placeholder="Enter keywords separated by commas"
                  helperText="Enter specific keywords separated by commas"
                />
              )}
              
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>Timeframe</Typography>
              <RadioGroup
                value={timeframeType}
                onChange={(e) => setTimeframeType(e.target.value)}
              >
                <FormControlLabel value="today" control={<Radio />} label="Today only" />
                <FormControlLabel value="days" control={<Radio />} label="Last X days" />
                <FormControlLabel value="weeks" control={<Radio />} label="Last X weeks" />
                <FormControlLabel value="months" control={<Radio />} label="Last X months" />
                <FormControlLabel value="lifetime" control={<Radio />} label="All Time" />
              </RadioGroup>
              
              {timeframeType !== 'today' && timeframeType !== 'lifetime' && (
                <Box sx={{ px: 3, mt: 2 }}>
                  <Slider
                    value={timeframeValue}
                    onChange={(e, newValue) => setTimeframeValue(newValue)}
                    aria-labelledby="timeframe-slider"
                    valueLabelDisplay="auto"
                    step={1}
                    marks
                    min={1}
                    max={timeframeType === 'days' ? 30 : timeframeType === 'weeks' ? 12 : 12}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {`${timeframeValue} ${timeframeType}`}
                  </Typography>
                </Box>
              )}
              
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>API Call Limit</Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Limit the number of API calls to control costs and scan time.
              </Typography>
              <Slider
                value={maxSearches}
                onChange={(e, newValue) => setMaxSearches(newValue)}
                aria-labelledby="api-call-slider"
                valueLabelDisplay="auto"
                step={50}
                marks={[
                  { value: 50, label: '50' },
                  { value: 250, label: '250' },
                  { value: 500, label: '500' }
                ]}
                min={50}
                max={500}
              />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button onClick={handleBack}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<ArrowForwardIcon />}
                >
                  Next
                </Button>
              </Box>
            </motion.div>
          )}
          
          {activeStep === 2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Typography variant="h6" gutterBottom>Review & Start Scan</Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Review your scan settings before starting.
              </Typography>
              
              <Paper variant="outlined" sx={{ p: 3, mt: 3, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', width: '100%' }}>
                    <Box sx={{ width: '33%' }}>
                      <Typography variant="subtitle2" color="text.secondary">Creator</Typography>
                    </Box>
                    <Box sx={{ width: '67%' }}>
                      <Typography variant="body2">{creatorName}</Typography>
                    </Box>
                  </Box>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Box sx={{ display: 'flex', width: '100%' }}>
                    <Box sx={{ width: '33%' }}>
                      <Typography variant="subtitle2" color="text.secondary">Content Type</Typography>
                    </Box>
                    <Box sx={{ width: '67%' }}>
                      <Typography variant="body2">
                        {contentType === 'all' ? 'All Content' : 
                         contentType === 'video' ? 'Videos' : 
                         contentType === 'image' ? 'Images' : 'Text'}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Box sx={{ display: 'flex', width: '100%' }}>
                    <Box sx={{ width: '33%' }}>
                      <Typography variant="subtitle2" color="text.secondary">Keywords</Typography>
                    </Box>
                    <Box sx={{ width: '67%' }}>
                      {keywordType === 'suggested' ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {[creatorName, `${creatorName} content`, `${creatorName} official`].map((keyword, index) => (
                            <Chip 
                              key={index} 
                              label={keyword} 
                              size="small" 
                              sx={{ mb: 1 }} 
                            />
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2">
                          {customKeywords.split(',').map(k => k.trim()).filter(Boolean).join(', ')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Box sx={{ display: 'flex', width: '100%' }}>
                    <Box sx={{ width: '33%' }}>
                      <Typography variant="subtitle2" color="text.secondary">Timeframe</Typography>
                    </Box>
                    <Box sx={{ width: '67%' }}>
                      <Typography variant="body2">
                        {timeframeType === 'today' 
                          ? 'Today only' 
                          : timeframeType === 'lifetime'
                          ? 'All Time'
                          : `Last ${timeframeValue} ${timeframeType}`}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Box sx={{ display: 'flex', width: '100%' }}>
                    <Box sx={{ width: '33%' }}>
                      <Typography variant="subtitle2" color="text.secondary">API Call Limit</Typography>
                    </Box>
                    <Box sx={{ width: '67%' }}>
                      <Typography variant="body2">{maxSearches} calls</Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>
              
              {isStartingScan ? (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    Starting scan, please wait...
                  </Typography>
                </Box>
              ) : (
                <>
                  {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {error}
                    </Alert>
                  )}
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                    <Button onClick={handleBack}>
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleStartScan}
                      startIcon={<SearchIcon />}
                    >
                      Start Scan
                    </Button>
                  </Box>
                </>
              )}
            </motion.div>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SimpleScanForm;
